import createClient from 'openapi-fetch';
import { getRequestEvent, isServer } from 'solid-js/web';
import type { paths } from './__generated__/database.ts';
import type { Client, ClientOptions } from 'openapi-fetch';

type Options = ClientOptions & {
	localStorageToken?: string;
};

const clientOptions: Options = {
	baseUrl: '/',
	credentials: 'include',
	mode: 'cors',
	referrerPolicy: 'origin',
	keepalive: true,
};

export type ApiClient = Client<paths, 'application/json'>;

export function createServerApiClient(config: ClientOptions, inputHeaders?: Record<string, string>): ApiClient {
	if (!isServer) {
		throw new Error('Cannot create server API client in the browser.');
	}

	const client: ApiClient = createClient<paths>({
		...clientOptions,
		...config,
		headers: inputHeaders,
		fetch: globalThis.fetch,
	});

	return client as ApiClient;
}

export function createApiClient(config: Options = {}) {
	const { localStorageToken, ...rest } = config;
	const client = createClient<paths>({
		...clientOptions,
		fetch: (init) => globalThis.fetch(init),
		...rest,
	}) as ApiClient;
	if (localStorageToken) {
		client.use({
			async onRequest({ request: req }) {
				if (isServer) {
					return req;
				}
				try {
					const token = window.localStorage?.getItem(localStorageToken);
					if (token) {
						req.headers.set('Authorization', `Bearer ${token}`);
					}
				} catch {
					// nothing
				}
				return req;
			},
		});
	}

	globalThis.__trBrowserClient = client;
	return getBrowserClient();
}

export function getApiClient(config?: Options): ApiClient {
	if (isServer) {
		// @ts-expect-error They forgot about this in the upstream defs…
		return getRequestEvent()!.locals.apiClient;
	}

	const client = getBrowserClient();
	if (!client) {
		return createApiClient(config);
	}
	return client;
}

function getBrowserClient() {
	return globalThis.__trBrowserClient as ApiClient;
}

declare global {
	var __trBrowserClient: ApiClient;
}
